/**
 * Normalizes seller identity text for matching Dolores users and saved sellers.
 */
export const read_seller_identity_key = (value) => {
    if (!["string", "number"].includes(typeof value)) {
        return "";
    }

    const key = `${value || ""}`.trim().replace(/\s+/g, " ").toLowerCase();

    return ["[object object]", "undefined", "null", "nan"].includes(key) ? "" : key;
};

const read_seller_text = (...values) => values.map((value) => {
    if (!["string", "number"].includes(typeof value)) {
        return "";
    }

    const text = `${value || ""}`.trim();

    return read_seller_identity_key(text) ? text : "";
}).find(Boolean) || "";

/**
 * Reads the direct Dolores subscription identity when the API provides one.
 */
export const read_seller_direct_sub_id = (seller) => {
    return read_seller_text(seller?.subId, seller?.sub_id, seller?.subscriptionId, seller?.subscription_id, seller?.subscription?.subId, seller?.subscription?.id, seller?.subscription?._id, seller?.sub);
};

/**
 * Reads the only value allowed inside seller select options.
 */
export const read_seller_select_value = (seller) => {
    return read_seller_text(read_seller_direct_sub_id(seller), seller?.userId, seller?.user_id, seller?.id, seller?._id);
};

/**
 * Reads the display name used for seller labels and avatars.
 */
export const read_seller_name = (seller) => {
    const fullName = [
        read_seller_text(seller?.firstname, seller?.firstName),
        read_seller_text(seller?.lastname, seller?.lastName)
    ].filter(Boolean).join(" ");

    return read_seller_text(fullName, seller?.name, seller?.fullName, seller?.displayName, seller?.username, seller?.email);
};

/**
 * Reads all keys that can recover a saved legacy seller from Dolores HR.
 */
export const read_seller_identity_keys = (seller) => {
    const directSellerKey = read_seller_identity_key(seller);

    return [
        read_seller_select_value(seller),
        directSellerKey,
        seller?.subId,
        seller?.sub_id,
        seller?.subscriptionId,
        seller?.subscription_id,
        seller?.subscription?.subId,
        seller?.subscription?.id,
        seller?.subscription?._id,
        seller?.sub,
        seller?.userId,
        seller?.user_id,
        seller?.id,
        seller?._id,
        seller?.sellerId,
        seller?.username,
        seller?.preferred_username,
        seller?.preferredUsername,
        seller?.email,
        seller?.name,
        seller?.fullName,
        seller?.displayName,
        read_seller_name(seller)
    ].map((value) => read_seller_identity_key(value)).filter(Boolean);
};

/**
 * Checks whether a saved seller resolves to one Dolores HR user.
 */
export const read_same_seller_identity = (seller, member) => {
    const sellerSubId = read_seller_direct_sub_id(seller);
    const memberSubId = read_seller_direct_sub_id(member);

    if (sellerSubId && memberSubId) {
        return sellerSubId === memberSubId;
    }

    const sellerKeys = read_seller_identity_keys(seller);
    const memberKeys = read_seller_identity_keys(member);

    return sellerKeys.some((sellerKey) => memberKeys.includes(sellerKey));
};

/**
 * Finds a Dolores HR member by the canonical seller select value.
 */
export const read_seller_member_by_sub_id = (members = [], sellerSubId = "") => {
    const selectedSubId = `${sellerSubId || ""}`.trim();

    return (members || []).find((member) => {
        return read_seller_select_value(member) === selectedSubId;
    }) || null;
};

/**
 * Resolves a saved seller object to the matching Dolores HR member.
 */
export const read_seller_member = (seller, members = []) => {
    return (members || []).find((member) => {
        return read_same_seller_identity(seller, member);
    }) || null;
};

/**
 * Reads the select value for a saved seller after HR resolution.
 */
export const read_seller_member_select_value = (seller, members = []) => {
    const sellerMember = read_seller_member(seller, members);

    return read_seller_select_value(sellerMember);
};

/**
 * Builds the canonical seller payload saved to Niffler.
 */
export const read_seller_payload = (seller) => {
    const subId = read_seller_select_value(seller);
    const userId = read_seller_text(seller?.userId, seller?.user_id, seller?.id, seller?._id);

    return {
        subId,
        subscriptionId: read_seller_text(seller?.subscriptionId, seller?.subscription_id, subId),
        userId,
        id: userId,
        username: read_seller_text(seller?.username, seller?.email),
        firstname: read_seller_text(seller?.firstname, seller?.firstName),
        lastname: read_seller_text(seller?.lastname, seller?.lastName),
        email: read_seller_text(seller?.email),
        avatarUrl: read_seller_text(seller?.avatarUrl, seller?.profileImage, seller?.image, seller?.picture, seller?.photo),
        name: read_seller_name(seller)
    };
};

/**
 * Reads Dolores HR users as valid dropdown option rows.
 */
export const read_seller_options = (members = []) => {
    return (members || []).map((member) => {
        return {
            key: read_seller_select_value(member),
            value: read_seller_select_value(member),
            label: read_seller_name(member),
            member
        };
    }).filter((option) => option.value);
};
